"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { captureAndUploadDefaultPoster } from "@/lib/capture-video-frame";
import { sha256File } from "@/lib/sha256-file";
import {
  deleteQueuedUpload,
  fileFromRecord,
  listQueuedUploads,
  patchQueuedUpload,
  putQueuedUpload,
} from "@/lib/upload-queue-store";
import { uploadWithProgress } from "@/lib/upload-with-progress";

export type BulkVideoMeta = {
  tagIds?: string[];
  sourceIds?: string[];
  year?: number;
  description?: string;
};

export type UploadItem = {
  id: string;
  fileName: string;
  phase:
    | "queued"
    | "hashing"
    | "checking"
    | "duplicate"
    | "presigning"
    | "uploading"
    | "creating"
    | "done"
    | "error";
  loaded: number;
  total: number;
  storageKey?: string;
  videoId?: string;
  errorMessage?: string;
  matchTitle?: string;
  matchVideoId?: string;
};

type QueueJob = {
  id: string;
  file: File;
  meta: BulkVideoMeta;
};

type PendingUpload = {
  file: File;
  meta: BulkVideoMeta;
  contentHash: string;
};

type UploadContextValue = {
  uploads: UploadItem[];
  enqueueUploads: (files: File[], meta: BulkVideoMeta) => void;
  cancelUpload: (id: string) => void;
  dismissUpload: (id: string) => void;
  clearCompleted: () => void;
  skipDuplicate: (id: string) => void;
  uploadAnyway: (id: string) => void;
};

const UploadContext = createContext<UploadContextValue | null>(null);

export function useUploads() {
  const ctx = useContext(UploadContext);
  if (!ctx) {
    throw new Error("useUploads must be used within UploadProvider");
  }
  return ctx;
}

function newUploadId(): string {
  return crypto.randomUUID();
}

function isAbortError(e: unknown): boolean {
  return (
    (e instanceof DOMException && e.name === "AbortError") ||
    (e instanceof Error && e.name === "AbortError")
  );
}

export function UploadProvider({
  children,
  userId,
}: {
  children: ReactNode;
  userId?: string | null;
}) {
  const router = useRouter();
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const abortControllers = useRef(new Map<string, AbortController>());
  const pendingUploads = useRef(new Map<string, PendingUpload>());
  const forceUpload = useRef(new Set<string>());
  const queueRef = useRef<QueueJob[]>([]);
  const activeIdRef = useRef<string | null>(null);
  const batchPending = useRef(0);
  const restoredUserId = useRef<string | null>(null);

  const patchUpload = useCallback((id: string, patch: Partial<UploadItem>) => {
    setUploads((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  }, []);

  const maybeRefresh = useCallback(() => {
    batchPending.current -= 1;
    if (batchPending.current <= 0) {
      batchPending.current = 0;
      router.refresh();
    }
  }, [router]);

  const dropStored = useCallback((id: string) => {
    void deleteQueuedUpload(id);
  }, []);

  const runPipeline = useCallback(
    async (id: string, file: File, meta: BulkVideoMeta) => {
      const controller = new AbortController();
      abortControllers.current.set(id, controller);
      let pausedForDuplicate = false;

      try {
        let contentHash = pendingUploads.current.get(id)?.contentHash;
        if (!contentHash) {
          patchUpload(id, { phase: "hashing", total: file.size });
          contentHash = await sha256File(file, controller.signal);
          void patchQueuedUpload(id, { contentHash });
        }

        if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");

        patchUpload(id, { phase: "checking", total: file.size });
        const checkRes = await fetch("/api/uploads/check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            kind: "video",
            fileName: file.name,
            sizeBytes: file.size,
            contentHash,
          }),
          signal: controller.signal,
        });
        const checkData = (await checkRes.json()) as {
          match?: "none" | "likely" | "exact";
          items?: { id: string; title: string }[];
          error?: string;
        };
        if (checkRes.ok && checkData.match === "exact" && checkData.items?.[0]) {
          const existing = checkData.items[0];
          if (!forceUpload.current.has(id)) {
            pausedForDuplicate = true;
            pendingUploads.current.set(id, { file, meta, contentHash });
            patchUpload(id, {
              phase: "duplicate",
              matchTitle: existing.title,
              matchVideoId: existing.id,
            });
            void patchQueuedUpload(id, {
              phase: "duplicate",
              contentHash,
              matchTitle: existing.title,
              matchVideoId: existing.id,
            });
            return;
          }
        }

        pendingUploads.current.delete(id);
        forceUpload.current.delete(id);

        const posterPromise = captureAndUploadDefaultPoster(
          file,
          controller.signal,
        );

        patchUpload(id, { phase: "presigning", total: file.size });

        const presignRes = await fetch("/api/uploads/presign", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            kind: "video",
            fileName: file.name,
            contentType: file.type || "video/mp4",
          }),
          signal: controller.signal,
        });

        const data = (await presignRes.json()) as {
          uploadUrl?: string;
          storageKey?: string;
          error?: string;
        };

        if (!presignRes.ok) {
          throw new Error(data.error ?? "Could not start upload");
        }
        if (!data.uploadUrl || !data.storageKey) {
          throw new Error("Bad response from server");
        }

        patchUpload(id, {
          phase: "uploading",
          loaded: 0,
          total: file.size,
          storageKey: data.storageKey,
        });

        await uploadWithProgress(
          data.uploadUrl,
          file,
          file.type || "video/mp4",
          (loaded, total) => {
            patchUpload(id, { phase: "uploading", loaded, total });
          },
          controller.signal,
        );

        patchUpload(id, { phase: "creating", loaded: file.size, total: file.size });

        const thumbnailKey = await posterPromise;
        if (controller.signal.aborted) {
          throw new DOMException("Aborted", "AbortError");
        }

        const completeRes = await fetch("/api/uploads/complete", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            kind: "video",
            fileName: file.name,
            storageKey: data.storageKey,
            tagIds: meta.tagIds,
            sourceIds: meta.sourceIds,
            year: meta.year,
            description: meta.description,
            sizeBytes: file.size,
            contentHash,
            ...(thumbnailKey ? { thumbnailKey } : {}),
          }),
          signal: controller.signal,
        });
        const completeData = (await completeRes.json()) as {
          id?: string;
          error?: string;
        };
        if (!completeRes.ok || !completeData.id) {
          throw new Error(completeData.error ?? "Could not save video");
        }

        patchUpload(id, {
          phase: "done",
          videoId: completeData.id,
          loaded: file.size,
          total: file.size,
        });
        dropStored(id);
      } catch (e) {
        if (controller.signal.aborted || isAbortError(e)) {
          dropStored(id);
          setUploads((prev) => prev.filter((item) => item.id !== id));
        } else {
          dropStored(id);
          const message = e instanceof Error ? e.message : "Upload failed";
          patchUpload(id, { phase: "error", errorMessage: message });
        }
      } finally {
        abortControllers.current.delete(id);
        if (!pausedForDuplicate) {
          pendingUploads.current.delete(id);
          forceUpload.current.delete(id);
          maybeRefresh();
        }
      }
    },
    [dropStored, maybeRefresh, patchUpload],
  );

  const pump = useCallback(() => {
    if (activeIdRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    activeIdRef.current = next.id;
    void (async () => {
      try {
        await runPipeline(next.id, next.file, next.meta);
      } finally {
        activeIdRef.current = null;
        pump();
      }
    })();
  }, [runPipeline]);

  useEffect(() => {
    if (!userId) return;
    if (restoredUserId.current === userId) return;
    let cancelled = false;

    void (async () => {
      const records = await listQueuedUploads(userId, "video");
      if (cancelled) return;
      restoredUserId.current = userId;
      if (records.length === 0) return;

      const restoredItems: UploadItem[] = [];
      const jobs: QueueJob[] = [];

      for (const record of records) {
        const file = fileFromRecord(record);
        const meta = (record.meta ?? {}) as BulkVideoMeta;
        restoredItems.push({
          id: record.id,
          fileName: record.fileName,
          phase: record.phase === "duplicate" ? "duplicate" : "queued",
          loaded: 0,
          total: record.size,
          matchTitle: record.matchTitle,
          matchVideoId: record.matchVideoId,
        });

        if (record.contentHash) {
          pendingUploads.current.set(record.id, {
            file,
            meta,
            contentHash: record.contentHash,
          });
        }

        if (record.phase === "duplicate" && record.contentHash) {
          continue;
        }
        jobs.push({ id: record.id, file, meta });
      }

      batchPending.current += restoredItems.length;
      queueRef.current.push(...jobs);
      setUploads((prev) => {
        const existing = new Set(prev.map((item) => item.id));
        const extra = restoredItems.filter((item) => !existing.has(item.id));
        return extra.length === 0 ? prev : [...prev, ...extra];
      });
      pump();
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, pump]);

  const enqueueUploads = useCallback(
    (files: File[], meta: BulkVideoMeta) => {
      if (files.length === 0) return;

      batchPending.current += files.length;
      let order = Date.now();

      const newItems: UploadItem[] = [];
      for (const file of files) {
        const id = newUploadId();
        queueRef.current.push({ id, file, meta });
        newItems.push({
          id,
          fileName: file.name,
          phase: "queued",
          loaded: 0,
          total: file.size,
        });
        if (userId) {
          void putQueuedUpload({
            id,
            userId,
            kind: "video",
            file,
            fileName: file.name,
            type: file.type || "video/mp4",
            size: file.size,
            meta,
            phase: "queued",
            order: order++,
          });
        }
      }

      setUploads((prev) => [...prev, ...newItems]);
      pump();
    },
    [pump, userId],
  );

  const cancelUpload = useCallback(
    (id: string) => {
      const queuedIndex = queueRef.current.findIndex((job) => job.id === id);
      if (queuedIndex >= 0) {
        queueRef.current.splice(queuedIndex, 1);
        dropStored(id);
        setUploads((prev) => prev.filter((item) => item.id !== id));
        maybeRefresh();
        return;
      }

      const controller = abortControllers.current.get(id);
      if (controller) {
        controller.abort();
      }
    },
    [dropStored, maybeRefresh],
  );

  const skipDuplicate = useCallback(
    (id: string) => {
      pendingUploads.current.delete(id);
      forceUpload.current.delete(id);
      abortControllers.current.delete(id);
      dropStored(id);
      setUploads((prev) => prev.filter((item) => item.id !== id));
      maybeRefresh();
    },
    [dropStored, maybeRefresh],
  );

  const uploadAnyway = useCallback(
    (id: string) => {
      const pending = pendingUploads.current.get(id);
      if (!pending) return;
      forceUpload.current.add(id);
      void patchQueuedUpload(id, { phase: "queued" });
      patchUpload(id, { phase: "queued" });
      queueRef.current.unshift({ id, file: pending.file, meta: pending.meta });
      pump();
    },
    [patchUpload, pump],
  );

  const dismissUpload = useCallback(
    (id: string) => {
      dropStored(id);
      setUploads((prev) => prev.filter((item) => item.id !== id));
    },
    [dropStored],
  );

  const clearCompleted = useCallback(() => {
    setUploads((prev) =>
      prev.filter((item) => item.phase !== "done" && item.phase !== "error"),
    );
  }, []);

  return (
    <UploadContext.Provider
      value={{
        uploads,
        enqueueUploads,
        cancelUpload,
        dismissUpload,
        clearCompleted,
        skipDuplicate,
        uploadAnyway,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
}
